import React, { useRef } from 'react'
import { useAppSelector } from '@common/app/hooks'
import { extractEventBaseUuid } from '@common/utils/extractEventBaseUuid'
import {
  InputLabel,
  List,
  ListItemButton,
  MenuItem,
  Select,
  Typography
} from '@linagora/twake-mui'
import { useI18n } from 'twake-i18n'
import { CalendarItemList } from './CalendarItemList'
import { useScreenSizeDetection } from '@common/useScreenSizeDetection'
import {
  MobileSelector,
  MobileSelectorHandle
} from '@common/components/MobileSelector'
import { CalendarName } from './CalendarName'

export const CalendarSelector: React.FC<{
  userId: string
  importTarget: string
  setImportTarget: (target: string) => void
}> = ({ userId, importTarget, setImportTarget }) => {
  const { t } = useI18n()
  const { isTooSmall: isMobile } = useScreenSizeDetection()
  const calendars = useAppSelector(state => state.calendars.list)
  const personalCalendars = Object.values(calendars).filter(
    cal => extractEventBaseUuid(cal.id) === userId
  )

  const selectorRef = useRef<MobileSelectorHandle>(null)
  const selectedCalendar = personalCalendars.find(
    cal => cal.id === importTarget
  )

  const handleMobileSelectCalendar = (calendarId: string): void => {
    setImportTarget(calendarId)
    selectorRef.current?.onClose()
  }

  if (isMobile) {
    return (
      <>
        <Typography variant="h6" sx={{ margin: 0, marginBottom: 1 }}>
          {t('calendar.import_to')}
        </Typography>
        <MobileSelector
          ref={selectorRef}
          displayText={
            importTarget === 'new' ? (
              t('calendar.new_calendar')
            ) : selectedCalendar ? (
              <CalendarName calendar={selectedCalendar} />
            ) : null
          }
        >
          <List sx={{ overflow: 'auto', flex: 1, pt: 0 }}>
            <ListItemButton onClick={() => handleMobileSelectCalendar('new')}>
              {t('calendar.new_calendar')}
            </ListItemButton>
            {personalCalendars.map(calendar => (
              <ListItemButton
                key={calendar.id}
                onClick={() => handleMobileSelectCalendar(calendar.id)}
              >
                <CalendarName calendar={calendar} />
              </ListItemButton>
            ))}
          </List>
        </MobileSelector>
      </>
    )
  }

  return (
    <>
      <InputLabel id="import-to-label">{t('calendar.import_to')}</InputLabel>
      <Select
        labelId="import-to-label"
        label={t('calendar.import_to')}
        value={importTarget}
        onChange={e => setImportTarget(e.target.value)}
      >
        <MenuItem value="new">{t('calendar.new_calendar')}</MenuItem>
        {CalendarItemList(personalCalendars)}
      </Select>
    </>
  )
}
